/**
 * The pager's API client.
 *
 * Same shape as the vault's, and for the same reasons: the base URL is
 * runtime configuration rather than baked into the bundle, and the token is
 * the PKCE session this app holds for **its own** Cognito client.
 *
 * That second one is the model, not a detail. Permission is decided by the
 * client a token was signed for, so a pager running on a token it found lying
 * around would be governed by whatever app that token belongs to — and this
 * app can text and ring every employee it can see.
 */

let tokenSource: (() => string | null) | null = null;
let onUnauthorized: (() => void) | null = null;

/**
 * What to do when the API refuses a token. Registered by the app.
 *
 * Here rather than calling `signIn` directly for two reasons. The kit is
 * plain JavaScript, so a typed module importing it has no declarations to
 * import — and more to the point, an API client that navigates the browser is
 * an API client with a view about the UI. It reports; the app decides.
 */
export function onUnauthorizedCall(handler: () => void) {
  onUnauthorized = handler;
}

export function useTokenFrom(source: () => string | null) {
  tokenSource = source;
}

function base(): string {
  const configured = (window as unknown as {
    KERNEL_CONFIG?: { oncallApi?: string };
  }).KERNEL_CONFIG?.oncallApi;
  if (!configured) {
    // Loud rather than a fallback to somebody else's deployment. A default of
    // the production URL means a misconfigured build talks to production and
    // looks fine.
    throw new Error(
      'no oncallApi in KERNEL_CONFIG — config.js is missing or stale',
    );
  }
  return configured.replace(/\/$/, '');
}

export class ApiError extends Error {
  status: number;
  body: any;
  constructor(status: number, body: any) {
    super(body?.error || body?.detail || `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

async function call(method: string, path: string, body?: unknown) {
  const token = tokenSource?.() ?? null;
  const response = await fetch(`${base()}${path}`, {
    method,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const text = await response.text();
  let parsed: any;
  try { parsed = text ? JSON.parse(text) : undefined; } catch { parsed = undefined; }

  if (response.status === 401) reauthenticate();
  if (!response.ok) throw new ApiError(response.status, parsed ?? {});
  return parsed;
}

let reauthenticating = false;

/**
 * A 401 means sign in again, not "HTTP 401" over a signed-in app bar.
 *
 * Two ways to hold a token this API will not take, and a person cannot tell
 * them apart:
 *
 * - it expired, which is the ordinary one;
 * - **the app was reinstalled**, which is the one this platform makes
 *   routine. A reinstall builds a *new* Cognito client at the same hostname,
 *   so every browser still holding a session is holding one signed for a
 *   client that no longer exists. The session restores, the app bar shows who
 *   you are, and every request is refused.
 *
 * That second case is not exotic here — it happens every time somebody
 * removes an app and installs it again, which is the whole point of a
 * library. It read as a broken app, and "sign out and back in" is not
 * something a product should need somebody to work out.
 *
 * Guarded so a genuinely unauthorised call cannot loop the browser between
 * this page and the sign-in page. Once per page load: if the fresh token is
 * refused too, the error surfaces and says so.
 */
function reauthenticate() {
  if (reauthenticating || !onUnauthorized) return;
  reauthenticating = true;
  // Dropped first. Signing in navigates away, and a stale token left in
  // storage is one `resume` would hand straight back on the way in.
  try { sessionStorage.removeItem('kernel.token'); } catch { /* private mode */ }
  onUnauthorized();
}

export const api = {
  me: () => call('GET', '/oncall/me'),
  // A number this org states over the directory's. It goes to the kernel as
  // `person.hold`, not to a table this app keeps — see the Me screen.
  setPhone: (phone: string) => call('PUT', '/oncall/me/phone', { phone }),
  clearPhone: () => call('DELETE', '/oncall/me/phone'),
  people: () => call('GET', '/oncall/people'),

  schedules: () => call('GET', '/oncall/schedules'),
  schedule: (id: string) => call('GET', `/oncall/schedules/${id}`),
  createSchedule: (body: unknown) => call('POST', '/oncall/schedules', body),
  updateSchedule: (id: string, body: unknown) =>
    call('PUT', `/oncall/schedules/${id}`, body),
  upcoming: (id: string) => call('GET', `/oncall/schedules/${id}/upcoming`),

  createSwap: (scheduleId: string, body: unknown) =>
    call('POST', `/oncall/schedules/${scheduleId}/swaps`, body),
  revokeSwap: (scheduleId: string, swapId: string) =>
    call('DELETE', `/oncall/schedules/${scheduleId}/swaps/${swapId}`),

  policies: () => call('GET', '/oncall/policies'),
  createPolicy: (body: unknown) => call('POST', '/oncall/policies', body),
  updatePolicy: (id: string, body: unknown) =>
    call('PUT', `/oncall/policies/${id}`, body),
  services: () => call('GET', '/oncall/services'),
  createService: (body: unknown) => call('POST', '/oncall/services', body),

  incidents: () => call('GET', '/oncall/incidents'),
  // A person saying "this is broken", as opposed to a monitor posting to
  // /oncall/intake. Same incident, same paging, different door.
  declare: (body: unknown) => call('POST', '/oncall/incidents', body),
  incident: (id: string) => call('GET', `/oncall/incidents/${id}`),
  acknowledge: (id: string) => call('POST', `/oncall/incidents/${id}/acknowledge`),
  resolve: (id: string, closeReason?: string) =>
    call('POST', `/oncall/incidents/${id}/resolve`, { closeReason }),
  note: (id: string, message: string) =>
    call('POST', `/oncall/incidents/${id}/notes`, { message }),
};
